import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import * as Switch from '@radix-ui/react-switch';
import { colors } from '../../style';

const sizes = {
  md: {
    trackHeight: '18px',
    trackWidth: '36px',
    thumbSize: '14px',
    thumbOffset: '4px',
    thumbTranslate: '18px',
  },
  sm: {
    trackHeight: '14px',
    trackWidth: '28px',
    thumbSize: '12px',
    thumbOffset: '3px',
    thumbTranslate: '14px',
  },
} as const;

interface SwitcherProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Switch.Root>,
  'onChange' | 'onCheckedChange' | 'checked' | 'defaultChecked' | 'value'
> {
  value?: boolean;
  label?: string;
  size?: keyof typeof sizes;
  onChange?: (value: boolean) => void;
  'data-testid'?: string | undefined;
}

const SwitcherComponent = ({
  className,
  value,
  disabled,
  onChange,
  label,
  id,
  size,
  'data-testid': testId,
  ...rest
}: SwitcherProps) => {
  const [localValue, setLocalValue] = React.useState(value);
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <Switch.Root
      {...rest}
      aria-label={label}
      aria-disabled={disabled}
      disabled={disabled}
      checked={!!localValue}
      data-value={!!localValue}
      onCheckedChange={() => {
        onChange?.(!localValue);
        setLocalValue(!localValue);
      }}
      id={id}
      className={classNames('box-switcher', className, { active: localValue }, { disabled })}
      data-testid={testId}
    >
      <Switch.Thumb className={classNames('toggle', { active: localValue })} />
    </Switch.Root>
  );
};

const Switcher = styled(SwitcherComponent)`
  box-sizing: content-box;
  margin: 0;
  text-transform: none;
  -webkit-appearance: button;
  padding: 2px;
  position: relative;
  display: inline-flex;
  height: ${props => sizes[props.size || 'md'].trackHeight};
  width: ${props => sizes[props.size || 'md'].trackWidth};
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 56px;
  background: ${colors.gray400};
  border: none;
  transition-property: background-color, border-color, color, fill, stroke;
  transition-duration: 0.2s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(.disabled) {
    background: ${colors.gray400};
    opacity: 0.9;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.actionColor};
    outline-offset: 2px;
  }

  &.active {
    background: ${props => props.theme.inputBorderColor ?? props.theme.actionColor};
    &:hover:not(.disabled) {
      background: ${props => props.theme.inputBorderColor ?? props.theme.actionColor};
      opacity: 0.9;
    }
  }

  .toggle {
    -webkit-text-size-adjust: 100%;
    tab-size: 4;
    font-family: inherit;
    font-size: 100%;
    text-transform: none;
    line-height: inherit;
    color: inherit;
    cursor: pointer;
    box-sizing: border-box;
    border: 1px solid transparent;
    position: absolute;
    top: ${props => sizes[props.size || 'md'].thumbOffset};
    left: ${props => sizes[props.size || 'md'].thumbOffset};
    transition: all 0.2s ease;
    pointer-events: none;
    display: inline-block;
    height: ${props => sizes[props.size || 'md'].thumbSize};
    width: ${props => sizes[props.size || 'md'].thumbSize};
    border-radius: 50%;

    background: ${colors.white};
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transform: translateX(0);

    &.active {
      transform: translateX(${props => sizes[props.size || 'md'].thumbTranslate});
      background: ${colors.white};
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: ${colors.gray100};
    border: 1px solid ${colors.gray600};

    .toggle {
      background-color: ${colors.gray400};
      box-shadow: none;
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;

export default Switcher;
